import { Field, Form, Formik, FormikErrors } from "formik"
import useTranslation from "next-translate/useTranslation"
import Link from "next/link"
import { useRouter } from "next/router"
import { Card, CardBody, CardHeader, CardText } from "reactstrap"

import { FeedbackPostType, IDiscussion, IFeedbackInvitation, IFeedbackPost, IFeedbackQuestion, IUser } from "@api/schema"
import FormikInput from "@components/common/form/FormikInput"
import GeneralButtonAndErrorArea from "@components/common/form/GeneralButtonAndErrorArea"
import TranslatedHtml from "@components/common/TranslatedHtml"
import { IFormikActions } from "@redux/helper/actions"
import { FeedbackHelper } from "@services/feedbackHelper"
import { Routes } from "@services/routes"
import { IValidationErrors, SubArrayValidationError } from "@services/validation"


interface IProps {
  cardId: string
  currentUser: IUser
  invitation: IFeedbackInvitation
  onSubmit: (discussion: IDiscussion, actions: IFormikActions) => void
  simplestPresentation?: boolean // only shows form + button
}

/**
 * This page provides a Form for answering questions of an FBI
 *
 * @todo re-test error messages
 */
const FeedbackInvitationQuestionForm: React.FC<IProps> = (props: IProps) => {
  const { cardId, invitation, currentUser, simplestPresentation = false } = props
  const { t } = useTranslation("common")
  const router = useRouter()
  const currentUrl = router.asPath

  const validate = (post: IFeedbackPost): FormikErrors<IFeedbackPost> => {
    if (post) {
      const errors: IValidationErrors = {}

      if (post.content && Object.keys(post.content).length > 0) {
        const contentErrorMessages: SubArrayValidationError = {}
        Object.entries(post.content).map((value) => {
          if (value[1].length > FeedbackHelper.MAX_FEEDBACK_POST_CONTENT_LENGTH) {
            contentErrorMessages[value[0]] = "validate.general.tooLong"
          }
        })
        // only provide errors, if at least one error occured
        if (Object.keys(contentErrorMessages).length > 0) {
          errors.content = contentErrorMessages
        }
      }

      return errors
    }
  }


  const onFormSubmit = (post: IFeedbackPost, actions: IFormikActions): void => {

    const newPost: IFeedbackPost = {
      content: post.content,
      type: FeedbackPostType.Hint, // default type for answers to an FeedbackInvitation: Hint
      discussion: {
        contentId: "", // no contentId, because its not an answer to a project content, but to an FBI
        feedbackInvitation: invitation["@id"],
        initialPost: null,
      }
    }

    const newDiscussion: IDiscussion = {
      contentId: "", // no contentId, because its not an answer to a project content, but to an FBI
      feedbackInvitation: invitation["@id"],
      initialPost: newPost,
    }

    props.onSubmit(newDiscussion, actions)
  }


  return <Formik<IFeedbackPost>
    initialValues={FeedbackHelper.createNewFeedbackPostFromFBI(invitation)}
    onSubmit={onFormSubmit}
    validate={validate}
    validateOnBlur={false}
    validateOnChange={false}
  >
    {({
      values,
      errors,
      handleSubmit,
      isSubmitting,
      isValidating
      // values,
    }) => <Form
      className="common-form"
      onSubmit={handleSubmit}
      data-testid={"FeedbackInvitationQuestionCardForm"}
    >
        <Card>
          {!simplestPresentation && <CardHeader>
            <p>
              {invitation.anonymousFeedback ?
                <TranslatedHtml content="feedback.discussion.anonymousFeedback.anonymous" />
                : currentUser // if the FBI does not allow anonymous feedback, show username if user is logged in or ask to log in
                  ? <TranslatedHtml content="feedback.discussion.anonymousFeedback.answersByUser" params={{ username: currentUser?.username }} />
                  : t("feedback.discussion.anonymousFeedback.loginToParticipate")
              }
            </p>
          </CardHeader>}
          <CardBody>
            {invitation.anonymousFeedback || currentUser
              ? <>
                {invitation.questions.map((q: IFeedbackQuestion) => {
                  // create an Input-Field for every question
                  return <Field
                    component={FormikInput}
                    key={"questionform_" + q.id}
                    help={q.explanation}
                    // the name of the input field matches the pattern "content.id"
                    name={FeedbackHelper.getFBIQuestionContentInputFieldName(q.id)}
                    // the id of the input field must matches the ID of the question
                    id={q.id}
                    label={q.question}
                    maxLength={FeedbackHelper.MAX_FEEDBACK_POST_CONTENT_LENGTH}
                    placeholder=""
                    required
                    type="textarea"
                    value={values.content[q.id]}
                  />

                })}
                <GeneralButtonAndErrorArea
                  errors={errors}
                  prefixGeneralErrorForm="feedback"
                  excludedFieldsGeneralErrorForm={["title", "content"]}
                  isSubmitting={isSubmitting}
                  isValidating={isValidating}
                  titleSubmitButton="form.feedback.send"
                  iconNameSubmitButton="to-do"
                />
              </>
              : <>
                <CardText className="text-center">
                  <Link href={Routes.Login + currentUrl ? "?redirectBack=" + encodeURIComponent(currentUrl + "#" + cardId) : ""}>
                    <a className="btn primary-btn">{t("goto.login")}</a>
                  </Link>
                </CardText>
                <CardText className="text-center">
                  <Link href={Routes.Registration}>
                    <a className="btn primary-btn">{t("goto.registration")}</a>
                  </Link>
                </CardText>
              </>
            }
          </CardBody>
        </Card>
      </Form>
    }
  </Formik>
}

export default FeedbackInvitationQuestionForm